import { parseISO } from "date-fns";
import {
  Check,
  ChevronLeft,
  Copy,
  ExternalLink,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
  UserRoundPen,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type {
  EditableTenantMembershipRole,
  TenantDetailUI,
  TenantMember,
  TenantProductConnection,
  TenantProductVisibility,
} from "~/features/admin/tenants/types";
import { EDITABLE_TENANT_MEMBERSHIP_ROLES, TENANT_PRODUCT_VISIBILITIES } from "~/features/admin/tenants/types";

interface TenantDetailPresenterProps {
  tenant: TenantDetailUI;
  /** External tenant-page URL, or null when it can't be built. */
  tenantUrl: string | null;
  onBack: () => void;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onAddMember: () => void;
  onRemoveMember: (member: TenantMember) => void;
  onChangeMemberRole: (member: TenantMember, role: EditableTenantMembershipRole) => void;
  onChangeOwner: () => void;
  onInstallProduct: () => void;
  onChangeVisibility: (connection: TenantProductConnection, visibility: TenantProductVisibility) => void;
  processing: boolean;
}

export function TenantDetailPresenter({
  tenant,
  tenantUrl,
  onBack,
  onEdit,
  onActivate,
  onDeactivate,
  onAddMember,
  onRemoveMember,
  onChangeMemberRole,
  onChangeOwner,
  onInstallProduct,
  onChangeVisibility,
  processing,
}: TenantDetailPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-(--content-max) space-y-5 p-8">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ChevronLeft />
        <span>{t("ui.backToList")}</span>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-dewey-title">{tenant.name}</h1>
          {tenant.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
          {tenant.isDeactivated ? (
            <Button type="button" variant="ghost" size="sm" onClick={onActivate} disabled={processing}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onDeactivate} disabled={processing}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      <SubHeader tenant={tenant} />

      <PropertiesTable tenant={tenant} tenantUrl={tenantUrl} onChangeOwner={onChangeOwner} />

      <ProductConnectionsSection
        connections={tenant.productConnections}
        onInstallProduct={onInstallProduct}
        onChangeVisibility={onChangeVisibility}
        // The backend rejects installs/updates on a deactivated tenant, so
        // don't offer the controls.
        disabled={processing || tenant.isDeactivated}
      />

      <MembersSection
        members={tenant.members}
        ownerUserId={tenant.owner?.id ?? null}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        onChangeMemberRole={onChangeMemberRole}
        disabled={processing}
      />
    </div>
  );
}

function SubHeader({ tenant }: { tenant: TenantDetailUI }) {
  const { t } = useTranslation();
  const items: { label: string; value: React.ReactNode }[] = [
    {
      label: t("model.common.id"),
      value: <CopyableText text={tenant.id} className="font-mono" />,
    },
  ];
  if (tenant.createdAt)
    items.push({
      label: t("model.common.createdAt"),
      value: t("date.long", { date: parseISO(tenant.createdAt) }),
    });
  if (tenant.updatedAt && tenant.updatedAt !== tenant.createdAt) {
    items.push({
      label: t("model.common.updatedAt"),
      value: t("date.long", { date: parseISO(tenant.updatedAt) }),
    });
  }
  if (tenant.deactivatedAt) {
    items.push({
      label: t("model.common.deactivatedAt"),
      value: t("date.long", { date: parseISO(tenant.deactivatedAt) }),
    });
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-12-regular text-fg-3">
      {items.map((item, i) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          {i > 0 && (
            <span aria-hidden className="text-border-strong">
              |
            </span>
          )}
          <span className="font-medium uppercase tracking-[0.04em]">{item.label}</span>
          <span className="text-fg-2 tabular-nums">{item.value}</span>
        </span>
      ))}
    </div>
  );
}

function PropertiesTable({
  tenant,
  tenantUrl,
  onChangeOwner,
}: {
  tenant: TenantDetailUI;
  tenantUrl: string | null;
  onChangeOwner: () => void;
}) {
  const { t } = useTranslation();
  const openTenantPageLabel = t("model.tenant.openTenantPage", {
    shortCode: tenant.shortCode,
  });
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("model.common.name"), value: tenant.name },
    {
      label: t("model.tenant.shortCode"),
      value: (
        <span className="inline-flex items-center gap-1.5">
          <span className="font-mono">{tenant.shortCode}</span>
          {tenantUrl && (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={tenantUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={openTenantPageLabel}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-xs text-primary-600 hover:bg-primary-50 hover:text-primary-700"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </TooltipTrigger>
              <TooltipContent>{openTenantPageLabel}</TooltipContent>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      label: t("model.organization.modelName"),
      value: (
        <span className="inline-flex items-baseline gap-1.5">
          <span>{tenant.organization.name}</span>
          <span className="font-mono text-12-regular text-fg-3">{tenant.organization.shortCode}</span>
        </span>
      ),
    },
    {
      label: t("model.tenant.allowAutoApproveChangeRequests"),
      value: tenant.allowAutoApproveChangeRequests ? (
        <Badge tone="primary">{String(true)}</Badge>
      ) : (
        <Badge tone="default">{String(false)}</Badge>
      ),
    },
    {
      label: t("model.tenant.owner"),
      value: (
        <span className="inline-flex flex-wrap items-center gap-2">
          {tenant.owner ? (
            <span className="inline-flex items-center gap-1.5">
              <span>{tenant.owner.displayName ?? tenant.owner.email}</span>
              {tenant.owner.displayName && <span className="text-12-regular text-fg-3">{tenant.owner.email}</span>}
              {tenant.owner.deactivatedAt && <Badge tone="default">{t("ui.deactivated")}</Badge>}
            </span>
          ) : (
            <Badge tone="default">{t("model.tenant.noOwner")}</Badge>
          )}
          <Button type="button" variant="outline" size="sm" onClick={onChangeOwner}>
            <UserRoundPen />
            <span>{t("feature.tenants.changeOwner")}</span>
          </Button>
        </span>
      ),
    },
  ];
  return (
    <div className="overflow-x-auto rounded-md border border-border-default bg-white">
      <table className="w-full border-collapse">
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border-default" : undefined}>
              <th className="w-56 bg-bg-subtle px-3.5 py-2 text-left text-12-medium text-fg-2 align-top capitalize">
                {row.label}
              </th>
              <td className="px-3.5 py-2 text-14-regular leading-[1.6] text-fg-1">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductConnectionsSection({
  connections,
  onInstallProduct,
  onChangeVisibility,
  disabled,
}: {
  connections: TenantProductConnection[];
  onInstallProduct: () => void;
  onChangeVisibility: (connection: TenantProductConnection, visibility: TenantProductVisibility) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-16-bold leading-tight capitalize">{t("model.tenant.productConnections")}</h2>
        <Button type="button" variant="outline" size="sm" onClick={onInstallProduct} disabled={disabled}>
          <Plus />
          <span className="capitalize">{t("feature.tenants.installProduct")}</span>
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border border-border-default bg-white">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-bg-subtle">
              <Th>{t("model.product.modelName")}</Th>
              <Th>{t("model.tenant.visibility")}</Th>
            </tr>
          </thead>
          <tbody>
            {connections.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-3.5 py-8 text-center text-14-regular text-fg-3">
                  {t("feature.tenants.noProductConnections")}
                </td>
              </tr>
            ) : (
              connections.map((connection) => (
                <tr key={connection.id} className="border-b border-border-default last:border-b-0">
                  <Td className="font-medium text-fg-1">
                    <span className="inline-flex items-center gap-1.5">
                      <span>{connection.productName}</span>
                      {connection.productDeactivatedAt && <Badge tone="default">{t("ui.deactivated")}</Badge>}
                    </span>
                  </Td>
                  <Td>
                    <Select
                      value={connection.visibility}
                      onValueChange={(value) => {
                        if (value !== connection.visibility) {
                          onChangeVisibility(connection, value as TenantProductVisibility);
                        }
                      }}
                      // The backend rejects updates for deactivated products.
                      disabled={disabled || connection.productDeactivatedAt !== null}
                    >
                      <SelectTrigger className="w-80">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Keep the dynamic keys visible to i18next:extract (removeUnusedKeys): */}
                        {/* t("model.tenant.visibilityLevels.private") t("model.tenant.visibilityLevels.organization") t("model.tenant.visibilityLevels.authenticated") */}
                        {TENANT_PRODUCT_VISIBILITIES.map((level) => (
                          <SelectItem key={level} value={level}>
                            {t(`model.tenant.visibilityLevels.${level}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MembersSection({
  members,
  ownerUserId,
  onAddMember,
  onRemoveMember,
  onChangeMemberRole,
  disabled,
}: {
  members: TenantMember[];
  ownerUserId: string | null;
  onAddMember: () => void;
  onRemoveMember: (member: TenantMember) => void;
  onChangeMemberRole: (member: TenantMember, role: EditableTenantMembershipRole) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-16-bold leading-tight capitalize">{t("model.user.modelNames")}</h2>
        <Button type="button" variant="outline" size="sm" onClick={onAddMember} disabled={disabled}>
          <Plus />
          <span className="capitalize">{t("ui.addX", { x: t("model.user.modelName") })}</span>
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border border-border-default bg-white">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-bg-subtle">
              <Th>{t("model.common.name")}</Th>
              <Th>{t("model.user.email")}</Th>
              <Th>{t("model.tenant.membershipRole")}</Th>
              <Th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3.5 py-8 text-center text-14-regular text-fg-3">
                  —
                </td>
              </tr>
            ) : (
              members.map((member) => (
                <tr key={member.id} className="border-b border-border-default last:border-b-0">
                  <Td className="font-medium text-fg-1">{member.displayName ?? ""}</Td>
                  <Td>{member.email}</Td>
                  <Td>
                    <Select
                      value={member.role}
                      onValueChange={(value) => {
                        if (value !== member.role) {
                          onChangeMemberRole(member, value as EditableTenantMembershipRole);
                        }
                      }}
                      disabled={disabled || member.id === ownerUserId}
                    >
                      <SelectTrigger className="w-40" aria-label={t("model.tenant.membershipRole")}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* t("model.tenant.membershipRoles.member") t("model.tenant.membershipRoles.manager") */}
                        {EDITABLE_TENANT_MEMBERSHIP_ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {t(`model.tenant.membershipRoles.${role}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Td>
                  <Td className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveMember(member)}
                      disabled={disabled || member.id === ownerUserId}
                      aria-label={t("ui.deleteX", {
                        x: member.displayName || member.email,
                      })}
                      className="text-danger hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CopyableText({ text, className }: { text: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success(t("feature.common.idCopied"));
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={className}>{text}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-5 w-5 items-center justify-center rounded-xs text-fg-3 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={t("ui.copyId")}
          >
            {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("ui.copyId")}</TooltipContent>
      </Tooltip>
    </span>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`border-b border-border-default px-3.5 py-2.5 text-left text-10-regular uppercase tracking-[0.06em] text-fg-2 ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3.5 py-2 text-14-regular align-middle text-fg-1 ${className ?? ""}`}>{children}</td>;
}
